/** Extracts player body geometry and wall-overlap correction from GameScene. */
import { Player } from '../entities/Player';
import { MAP_HEIGHT_PX, MAP_WIDTH_PX } from '../utils/Constants';
import { WallPhysicsSystem } from '../systems/WallPhysicsSystem';
import { DebugLog } from '../debug/DebugLog';

/** Body geometry descriptor for wall physics checks (all values in world/display pixels). */
export interface PlayerBodyGeometry {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  displayOriginX: number;
  displayOriginY: number;
}

/** Build body geometry descriptor from the player's current physics body and sprite scale. */
export function getPlayerBodyGeometry(player: Player): PlayerBodyGeometry | null {
  const body = player.body;
  if (!body) return null;
  return {
    width: body.width,
    height: body.height,
    offsetX: body.offset.x * player.sprite.scaleX,
    offsetY: body.offset.y * player.sprite.scaleY,
    displayOriginX: player.sprite.displayOriginX * player.sprite.scaleX,
    displayOriginY: player.sprite.displayOriginY * player.sprite.scaleY,
  };
}

/**
 * Per-frame safety net: if the player body overlaps any wall tile, push them out.
 * If the initial correction still leaves the body in a wall or off-map, teleport
 * to the nearest clear position instead.
 */
export function resolvePlayerWallOverlap(player: Player, wallPhysics: WallPhysicsSystem): void {
  const body = player.body;
  if (!body) return;

  const correction = wallPhysics.computeWallCorrection(body);
  if (!correction) return;

  DebugLog.throttled('wall-correction', 200, 'physics',
    `WALL-PUSH dx=${correction.dx.toFixed(1)} dy=${correction.dy.toFixed(1)} tiles=${correction.tiles} ${correction.bodyBounds} vel=(${body.velocity.x.toFixed(0)},${body.velocity.y.toFixed(0)})`);

  player.sprite.x += correction.dx;
  player.sprite.y += correction.dy;
  body.x += correction.dx;
  body.y += correction.dy;
  if (correction.dx !== 0) body.velocity.x = 0;
  if (correction.dy !== 0) body.velocity.y = 0;

  // Safety: if correction pushed body off-map or still inside a wall (cascade),
  // teleport to the nearest clear position instead.
  if (body.x < 0 || body.y < 0 ||
      body.x + body.width > MAP_WIDTH_PX ||
      body.y + body.height > MAP_HEIGHT_PX ||
      wallPhysics.computeWallCorrection(body) !== null) {
    const geo = getPlayerBodyGeometry(player);
    if (geo) {
      const safe = wallPhysics.findNearestClearPosition(
        player.sprite.x, player.sprite.y, geo);
      if (safe) {
        DebugLog.log('physics', `WALL-TELEPORT from (${player.sprite.x.toFixed(0)},${player.sprite.y.toFixed(0)}) to (${safe.x},${safe.y})`);
        player.body.reset(safe.x, safe.y);
      }
    }
  }
}
